/**
 * UsageStatsRAGSection Component - RAG statistics section
 *
 * Simplified and cleaner version showing key RAG metrics
 */

import React from 'react';
import { Database, FileText } from 'lucide-react';
import type { Theme } from '../../themes/theme';
import type { UsageStatsProps } from './types/stats.types';

interface UsageStatsRAGSectionProps {
  ragStats?: UsageStatsProps['ragStats'];
  formatNumber: (num: number | undefined | null) => string;
  theme: Theme;
}

export const UsageStatsRAGSection: React.FC<UsageStatsRAGSectionProps> = ({
  ragStats,
  formatNumber,
  theme,
}) => {
  if (!ragStats) return null;

  const getShortDocName = (name: string): string => {
    const shortName = name.split('/').pop() || name;
    if (shortName.includes('_')) {
      const parts = shortName.split('_');
      const type = parts.find((p) => p === 'ANALISIS' || p === 'FECU');
      const period = parts.find((p) => /^\d{4}|[a-z]{3}\d{4}/i.test(p));
      if (type || period) {
        const typeLabel = type === 'ANALISIS' ? 'Análisis' : type || '';
        return `${typeLabel} ${period || ''}`.trim();
      }
    }
    return shortName.length > 25 ? shortName.substring(0, 22) + '...' : shortName;
  };

  const hasDocDistribution =
    ragStats.document_distribution?.final_context &&
    Object.keys(ragStats.document_distribution.final_context).length > 0;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div
        className={`flex items-center gap-1.5 ${theme.colors.text.primary} text-[10px] font-medium`}
      >
        <Database className="w-3 h-3 text-green-400" />
        <span>Contexto RAG</span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[9px]">
        {ragStats.total_chunks !== undefined && (
          <div className={`flex justify-between ${theme.colors.text.secondary}`}>
            <span>Chunks procesados:</span>
            <span className={theme.colors.text.primary}>{formatNumber(ragStats.total_chunks)}</span>
          </div>
        )}
        {ragStats.final_with_neighbors !== undefined && (
          <div className={`flex justify-between ${theme.colors.text.secondary}`}>
            <span>Chunks usados:</span>
            <span className={theme.colors.text.primary}>
              {formatNumber(ragStats.final_with_neighbors)}
            </span>
          </div>
        )}
        {ragStats.page_count !== undefined && (
          <div className={`flex justify-between ${theme.colors.text.secondary}`}>
            <span>Páginas:</span>
            <span className={theme.colors.text.primary}>{formatNumber(ragStats.page_count)}</span>
          </div>
        )}
        {ragStats.citation_analysis && (
          <>
            <div className={`flex justify-between ${theme.colors.text.secondary}`}>
              <span>Docs en contexto:</span>
              <span className={theme.colors.text.primary}>
                {ragStats.citation_analysis.documents_in_context_count}
              </span>
            </div>
            <div className={`flex justify-between ${theme.colors.text.secondary}`}>
              <span>Docs citados:</span>
              <span className={theme.colors.text.primary}>
                {ragStats.citation_analysis.documents_cited_count}
              </span>
            </div>
            {ragStats.citation_analysis.citation_coverage > 0 && (
              <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                <span>Cobertura:</span>
                <span className={theme.colors.text.primary}>
                  {(ragStats.citation_analysis.citation_coverage * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Document Distribution */}
      {hasDocDistribution && (
        <div className={`pt-2 border-t ${theme.colors.border.primary}/20`}>
          <div
            className={`flex items-center gap-1.5 ${theme.colors.text.primary} text-[9px] font-medium mb-1`}
          >
            <FileText className="w-3 h-3 text-blue-400" />
            <span>Documentos en contexto</span>
          </div>
          <div className="space-y-0.5">
            {Object.entries(ragStats.document_distribution!.final_context || {})
              .slice(0, 5)
              .map(([docName, dist]) => (
                <div
                  key={docName}
                  className={`flex items-center justify-between gap-2 text-[9px] ${theme.colors.text.secondary}`}
                >
                  <span className="truncate flex-1" title={docName}>
                    {getShortDocName(docName)}
                  </span>
                  <span className={`${theme.colors.text.primary} flex-shrink-0`}>
                    {(dist as any).chunks} chunks
                    {(dist as any).unique_pages > 0 && ` / ${(dist as any).unique_pages} pág`}
                  </span>
                </div>
              ))}
            {Object.keys(ragStats.document_distribution!.final_context || {}).length > 5 && (
              <div className={`text-[8px] ${theme.colors.text.secondary}/50 italic`}>
                +{Object.keys(ragStats.document_distribution!.final_context || {}).length - 5}{' '}
                documentos más
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Chunks by Similarity */}
      {ragStats.chunks_details && ragStats.chunks_details.length > 0 && (
        <div className={`pt-2 border-t ${theme.colors.border.primary}/20`}>
          <div className={`text-[9px] font-medium ${theme.colors.text.primary} mb-1`}>
            Top chunks por similitud
          </div>
          <div className="space-y-0.5">
            {[...ragStats.chunks_details]
              .filter((c) => c.similarity_score !== null)
              .sort((a, b) => (b.similarity_score || 0) - (a.similarity_score || 0))
              .slice(0, 5)
              .map((chunk, idx) => (
                <div
                  key={chunk.chunk_id || idx}
                  className={`flex items-center gap-2 text-[9px] ${theme.colors.text.secondary}`}
                >
                  <span className={`font-mono ${theme.colors.text.primary}`}>
                    {(chunk.similarity_score || 0).toFixed(2)}
                  </span>
                  <span className="truncate flex-1">{getShortDocName(chunk.source)}</span>
                  {chunk.page && (
                    <span className={`flex-shrink-0 ${theme.colors.text.secondary}/70`}>
                      p.{chunk.page}
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
